'use client'

import {useEscapeKey, useFocusTrap} from '@local/hooks'
import {cx} from '@local/utils/cx'
import {type ReactNode, forwardRef, ForwardedRef, ElementType} from 'react'

export interface SlideoutProps {
	isOpen: boolean
	onClickClose: () => void
	children: ReactNode
	className?: string
	containerClassName?: string
	backdropClassName?: string
	variant?: 'left' | 'right'
	childrenProps?;
	as?: ElementType
}

export const Slideout = forwardRef(
	(
		{
			isOpen,
			as = 'div',
			children,
			className,
			backdropClassName,
			containerClassName,
			variant = 'left',
			childrenProps = {},
			onClickClose,
		}: SlideoutProps,
		ref:ForwardedRef<HTMLDivElement>,
	) => {
		// const setIsLocked = useScrollStore(state => state.setIsLocked)

		const trapRef = useFocusTrap({
			includeContainer: true,
			initialFocus: 'container',
			returnFocus: true,
			updateNodes: true,
			isActive: isOpen,
			onClickClose,
		})

		useEscapeKey(onClickClose)

		const Tag = as

		return (
			<div
				ref={ref}
				className={cx(
					'fixed z-[9999] isolate inset-0',
					'transition-[visibility] duration-700',
					isOpen ? 'visible pointer-events-auto mr-[var(--sbw)]' : 'invisible pointer-events-none',
					containerClassName,
				)}>
				<div
					className={cx(
						'slideout-container',
						'flex bg-cream absolute top-0 h-full w-full lg:span-w-4-wide z-9',
						'transition-[visibility,clip-path] duration-500 origin-top will-change-[clip-path]',
						variant === 'left' && 'left-0 -translate-x-full',
						variant === 'right' && 'right-0 translate-x-full',
						isOpen
							? 'delay-150 visible pointer-events-all ease-expo-out [clip-path:inset(0_-100%)]'
							: '[clip-path:inset(0_0)] invisible pointer-events-none ease-quart-inout',
					)}
					ref={trapRef}>
					<Tag
						{...childrenProps}
						className={cx(
							'bg-white',
							'flex flex-col w-full h-full margin-px-1',
							variant === 'left' && 'translate-x-full',
							variant === 'right' && '-translate-x-full',
							className,
						)}>
						{children}
					</Tag>
				</div>

				{/* backdrop */}
				<div
					onClick={onClickClose}
					className={cx(
						'absolute z-0 inset-0 bg-black/50',
						'transition-opacity duration-700',
						isOpen ? 'opacity-100 ease-quart-out' : 'opacity-0 ease-quart-inout',
						backdropClassName,
					)} />
			</div>
		)
	},
)
